// The in-chat outreach preview card — pure helpers shared by the chat state (use-chat), the BFF
// history mapping (messages API), and the card UI (MessageRow/OutreachPreviewCard). The wire shape is
// the harness contract: the ASSISTANT reply of a turn that stopped at the outreach approval gate
// carries `metadata.outreachPreviewCard` (and the SSE `messageEnd` event carries the same card
// in-band, like `choices`). Its actions (Send, Edit, …) are the reply's ordinary `choices` chips,
// minted from the card's own labels — nothing here renders them.
//
// Every string on the card arrives final and already localized by the server (the captions too), so
// this surface needs no i18n keys for it. Deliberately dependency-free (mirrors lib/opportunity-card.ts):
// the validation here MIRRORS common-lib's `readOutreachPreviewCard` rather than importing it. Keep
// the two in step.

export const OUTREACH_PREVIEW_CHANNELS = ['whatsapp', 'sms', 'email', 'gmail', 'app'] as const;
export type OutreachPreviewChannel = (typeof OUTREACH_PREVIEW_CHANNELS)[number];

export const OUTREACH_PREVIEW_DELIVERIES = ['verbatim', 'wrapped', 'contextual'] as const;
export type OutreachPreviewDelivery = (typeof OUTREACH_PREVIEW_DELIVERIES)[number];

export type OutreachPreviewEntryData = {
  name: string;
  relation?: string;
  address?: string;
  symphonyUser?: boolean;
  stateLine: string;
  delivery: OutreachPreviewDelivery;
  body: string;
  frameBefore?: string;
  frameAfter?: string;
  leadTemplate?: string;
  followsLabel?: string;
  subject?: string;
  from?: string;
  files?: string[];
  footnote?: string;
};

export type OutreachPreviewGroupData = {
  channel: OutreachPreviewChannel;
  channelLabel: string;
  entries: OutreachPreviewEntryData[];
};

export type OutreachPreviewCardData = {
  groups: OutreachPreviewGroupData[];
  labels: { badge: string; subject: string; from: string; files: string };
  actions: string[];
};

const nonEmpty = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;
const trimmed = (value: unknown): string | undefined => (nonEmpty(value) ? value.trim() : undefined);
// The template frame meets the body edge to edge, so its own spacing is part of the text and is kept.
const verbatim = (value: unknown): string | undefined => (nonEmpty(value) ? value : undefined);
const stringList = (value: unknown): string[] | undefined => {
  const items = Array.isArray(value) ? value.filter(nonEmpty).map((item) => item.trim()) : [];
  return items.length > 0 ? items : undefined;
};

function readEntry(raw: unknown): OutreachPreviewEntryData | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const entry = raw as Record<string, unknown>;
  if (!nonEmpty(entry.name) || typeof entry.body !== 'string' || !nonEmpty(entry.stateLine)) return undefined;
  const delivery = (OUTREACH_PREVIEW_DELIVERIES as readonly string[]).includes(entry.delivery as string)
    ? (entry.delivery as OutreachPreviewDelivery)
    : 'verbatim';
  const optional: Partial<OutreachPreviewEntryData> = {
    relation: trimmed(entry.relation),
    address: trimmed(entry.address),
    frameBefore: verbatim(entry.frameBefore),
    frameAfter: verbatim(entry.frameAfter),
    leadTemplate: verbatim(entry.leadTemplate),
    followsLabel: trimmed(entry.followsLabel),
    subject: trimmed(entry.subject),
    from: trimmed(entry.from),
    files: stringList(entry.files),
    footnote: trimmed(entry.footnote),
  };
  return {
    name: entry.name.trim(),
    stateLine: entry.stateLine.trim(),
    delivery,
    body: entry.body,
    ...(entry.symphonyUser === true ? { symphonyUser: true } : {}),
    ...Object.fromEntries(Object.entries(optional).filter(([, v]) => v !== undefined)),
  };
}

/**
 * Read + sanitize `metadata.outreachPreviewCard` off a raw message entity.
 *
 * A group whose channel is unknown, or that has no readable entry, is dropped; a card left with no
 * group renders nothing. Missing captions fall back to English rather than to blanks, because a
 * caption in the wrong language is a smaller failure than a card with none, and the server always
 * sends them anyway.
 */
export function readOutreachPreviewCard(metadata: unknown): OutreachPreviewCardData | undefined {
  const raw = (metadata as { outreachPreviewCard?: unknown } | undefined)?.outreachPreviewCard;
  if (!raw || typeof raw !== 'object') return undefined;
  const card = raw as Record<string, unknown>;
  if (!Array.isArray(card.groups)) return undefined;
  const groups: OutreachPreviewGroupData[] = [];
  for (const rawGroup of card.groups) {
    if (!rawGroup || typeof rawGroup !== 'object') continue;
    const group = rawGroup as Record<string, unknown>;
    if (!(OUTREACH_PREVIEW_CHANNELS as readonly string[]).includes(group.channel as string) || !Array.isArray(group.entries)) continue;
    const entries = group.entries.map(readEntry).filter((entry): entry is OutreachPreviewEntryData => entry !== undefined);
    if (entries.length === 0) continue;
    groups.push({
      channel: group.channel as OutreachPreviewChannel,
      channelLabel: trimmed(group.channelLabel) ?? (group.channel as string),
      entries,
    });
  }
  if (groups.length === 0) return undefined;
  const labels = (card.labels && typeof card.labels === 'object' ? card.labels : {}) as Record<string, unknown>;
  return {
    groups,
    labels: {
      badge: trimmed(labels.badge) ?? 'Symphony user',
      subject: trimmed(labels.subject) ?? 'Subject',
      from: trimmed(labels.from) ?? 'From',
      files: trimmed(labels.files) ?? 'Attached',
    },
    actions: stringList(card.actions) ?? [],
  };
}
